import { useCallback, useEffect, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client";
import type { CellContext, ColumnDef } from "@tanstack/react-table";
import Table, {
  TableHeading,
  TableControls,
  TableFooter,
  buildAvatarDetailsColumn,
  buildCopyTextColumn,
  buildExpandableCountColumn,
  buildTagColumn
} from "@/components/Table";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import SearchInput from "@/components/SearchInput";
import EmptyState from "@/components/EmptyState";
import SegmentedButton from "@/components/SegmentedButton/SegmentedButton";
import usePagination from "@/hooks/usePagination";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { toFormat } from "@/lib/dateHelpers";
import { showErrorSnackbar } from "@/lib/snackbarUtils";
import { useHasRoles } from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { EVENT_REGISTRANTS_QUERY } from "../../../queries";
import { RegistrationStatus, type EventRegistrants } from "../../../types";
import { UPDATE_EVENT_SESSIONS_REGISTRATION_ATTENDANCES_MUTATION } from "../queries";
import { useAttendanceEventQueryDocument } from "../../../useEventDetailQuery";
import type { EventWrapper, FinalizeAttendanceRow, SessionAttendanceEntry } from "../types";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

export default function FinalizeAttendanceTable({ eventSlug }: { eventSlug: string }) {
  const { isCompactLayout } = useIsCompactLayout();
  const { trackEvent } = useAmplitude();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });

  useEffect(() => {
    resetPagination();
  }, [debouncedSearch, resetPagination]);

  const { data, loading } = useQuery<EventRegistrants>(EVENT_REGISTRANTS_QUERY, {
    variables: {
      eventSlug,
      status: RegistrationStatus.REGISTERED,
      ...paginationVariables,
      ...(debouncedSearch ? { search: debouncedSearch } : {})
    }
  });

  const eventDetailQuery = useAttendanceEventQueryDocument();

  const { data: eventDataResponse } = useQuery<EventWrapper>(eventDetailQuery, {
    variables: { slug: eventSlug }
  });
  const eventData = eventDataResponse?.event;

  const canEditAttendance = useHasRoles([ROLE.EDUCATION_STAFF, ROLE.GLOBAL_ADMIN]);

  const sessionMetadataMap = useMemo(() => {
    const map = new Map<string, { sessionId: string; startsAt: string; endsAt: string }>();
    eventData?.sessions?.forEach((session) => {
      session.registrations.forEach((sessionRegistration) => {
        map.set(sessionRegistration.id, {
          sessionId: session.id,
          startsAt: session.startsAt,
          endsAt: session.endsAt
        });
      });
    });
    return map;
  }, [eventData]);

  const connection = data?.eventRegistrants;

  const rows: FinalizeAttendanceRow[] = useMemo(() => {
    return (connection?.edges ?? []).map(({ node }) => {
      const sessionRegistrations = node.sessionRegistrations ?? [];
      const sessionAttendance: SessionAttendanceEntry[] = sessionRegistrations
        .map((sessionRegistration) => {
          const metadata = sessionMetadataMap.get(sessionRegistration.id);
          return {
            id: sessionRegistration.id,
            attended: sessionRegistration.attended,
            sessionId: metadata?.sessionId ?? "",
            startsAt: metadata?.startsAt ?? "",
            endsAt: metadata?.endsAt ?? ""
          };
        })
        .sort((a, b) => (a.startsAt < b.startsAt ? -1 : a.startsAt > b.startsAt ? 1 : 0));

      const totalSessions = sessionAttendance.length;
      const attendedCount = sessionAttendance.filter((session) => session.attended === true).length;

      return {
        id: node.id,
        pgaId: node.pgaId,
        registrantName: node.registrantName,
        profilePhoto: node.profilePhoto,
        programType: node.programType,
        fulfillableRequirementId: node.fulfillableRequirementId || "No Requirement",
        attendedCount,
        totalSessions,
        isComplete: totalSessions > 0 && attendedCount === totalSessions,
        sessionAttendance
      };
    });
  }, [connection, sessionMetadataMap]);

  const [updateEventSessionsRegistrationAttendances] = useMutation(
    UPDATE_EVENT_SESSIONS_REGISTRATION_ATTENDANCES_MUTATION
  );

  const handleAttendanceUpdate = useCallback(
    async (sessionRegistrationId: string, value: string | null) => {
      const attended = value === "true" ? true : value === "false" ? false : null;

      try {
        const { data: mutationData } = await updateEventSessionsRegistrationAttendances({
          variables: { attendances: [{ eventSessionRegistrationId: sessionRegistrationId, attended }] },
          refetchQueries: [
            {
              query: EVENT_REGISTRANTS_QUERY,
              variables: {
                eventSlug,
                status: RegistrationStatus.REGISTERED,
                ...paginationVariables,
                ...(debouncedSearch ? { search: debouncedSearch } : {})
              }
            }
          ]
        });
        if (mutationData?.updateEventSessionsRegistrationAttendances?.success) {
          trackEvent(
            EVENT_ANALYTICS_EVENTS.attendanceUpdated,
            getEventAnalyticsData({
              source: "attendance",
              eventId: eventData?.id,
              eventSlug,
              action: "update",
              count: 1,
              ...(attended !== null ? { attended } : {})
            })
          );
        }
      } catch {
        showErrorSnackbar("Failed to update attendance.");
      }
    },
    [
      updateEventSessionsRegistrationAttendances,
      eventSlug,
      paginationVariables,
      debouncedSearch,
      trackEvent,
      eventData?.id
    ]
  );

  const columns: ColumnDef<FinalizeAttendanceRow, unknown>[] = useMemo(
    () => [
      ...buildAvatarDetailsColumn<FinalizeAttendanceRow>({
        isCompactLayout,
        getTitle: (row) => row.registrantName,
        getSubtitle: (row) => row.programType,
        subtitleLabel: "Program",
        getPicture: (row) => row.profilePhoto || undefined,
        header: "Name",
        sortType: "text"
      }),
      buildCopyTextColumn<FinalizeAttendanceRow>({
        accessorKey: "pgaId",
        header: "PGA ID",
        copyFieldLabel: "PGA ID",
        getText: (row) => row.pgaId,
        enableSorting: false
      }),
      buildExpandableCountColumn<FinalizeAttendanceRow>({
        accessorKey: "sessionAttendance",
        header: "Session Attendance",
        getCount: (row) => row.attendedCount,
        getTotal: (row) => row.totalSessions
      }),
      buildTagColumn<FinalizeAttendanceRow>({
        accessorKey: "status",
        header: "Status",
        getTagLabel: (row) => (row.isComplete ? "Complete" : "Incomplete"),
        getTagType: (row) => (row.isComplete ? "success" : "warn")
      }),
      buildCopyTextColumn<FinalizeAttendanceRow>({
        accessorKey: "fulfillableRequirementId",
        header: "Education Requirement",
        copyFieldLabel: "Education Requirement",
        getText: (row) => row.fulfillableRequirementId,
        enableSorting: false
      })
    ],
    [isCompactLayout]
  );

  const sessionColumns: ColumnDef<SessionAttendanceEntry, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "startsAt",
        header: "Session Date",
        enableSorting: false,
        cell: ({ row }: CellContext<SessionAttendanceEntry, unknown>) => toFormat(row.original.startsAt) || "--"
      },
      {
        id: "attendance",
        header: "Attendance",
        enableSorting: false,
        size: 280,
        cell: ({ row }: CellContext<SessionAttendanceEntry, unknown>) => (
          <SegmentedButton
            showCheckmark
            allowDeselect
            options={[
              { label: "Present", value: "true" },
              { label: "Absent", value: "false" }
            ]}
            value={row.original.attended === null ? null : String(row.original.attended)}
            onChange={(value) => handleAttendanceUpdate(row.original.id, value)}
            disabled={!canEditAttendance}
          />
        )
      }
    ],
    [canEditAttendance, handleAttendanceUpdate]
  );

  return (
    <>
      <TableHeading
        heading="Finalize Attendance"
        body="Review each registrant's session attendance and resulting completion status."
      />
      <Table
        data={rows}
        loading={loading}
        columns={columns}
        hasActiveFilters={!!debouncedSearch}
        getRowCanExpand={(row) => row.original.totalSessions > 0}
        renderExpandedRow={({ row }) => (
          <TableDetailsPanel
            data={row.original.sessionAttendance}
            columns={sessionColumns}
            title={`Sessions (${row.original.sessionAttendance.length})`}
          />
        )}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Registrants" caption="This event does not have any registrants." iconName="person" />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results" caption="Try a different search." iconName="search" />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls pagination={createHeaderPagination(connection, { loading })} onSortClick={onSortClick}>
            <SearchInput
              query={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                resetPagination();
              }}
              onClear={() => {
                clearSearch();
                resetPagination();
              }}
              placeholder="Search Registrants"
            />
          </TableControls>
        )}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(connection, { loading })}
            totalRowCount={connection?.totalCount ?? rows.length}
          />
        )}
      />
    </>
  );
}
